/**
 * Location Context
 *
 * Manages organization and location selection for multi-tenant support
 * Provides location filtering and access control across the application
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { reportCompanyAccessFailure } from '../services/companyDashboardScope';
import { sortByName } from '../utils/sortByName';

const LocationContext = createContext({});

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
};

// Storage keys for persisting selections
const STORAGE_KEY_ORG = 'selectedOrgId';
const STORAGE_KEY_LOCATION = 'selectedLocationId';

export const LocationProvider = ({ children }) => {
  const { user, currentUser, getAuthHeaders, isDeveloper, selectedSiteGroup, supportPicking } = useAuth();

  // Current selections - initialize from localStorage if available
  const [selectedOrgId, setSelectedOrgIdState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_ORG) || null;
    } catch {
      return null;
    }
  });
  const [selectedLocationId, setSelectedLocationIdState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_LOCATION) || null;
    } catch {
      return null;
    }
  });

  // Wrapper functions to persist to localStorage
  const setSelectedOrgId = useCallback((orgId) => {
    setSelectedOrgIdState(orgId);
    try {
      if (orgId) {
        localStorage.setItem(STORAGE_KEY_ORG, orgId);
      } else {
        localStorage.removeItem(STORAGE_KEY_ORG);
      }
    } catch (e) {
      console.warn('[LocationContext] Failed to persist orgId to localStorage:', e);
    }
  }, []);

  const setSelectedLocationId = useCallback((locationId) => {
    setSelectedLocationIdState(locationId);
    try {
      if (locationId) {
        localStorage.setItem(STORAGE_KEY_LOCATION, locationId);
      } else {
        localStorage.removeItem(STORAGE_KEY_LOCATION);
      }
    } catch (e) {
      console.warn('[LocationContext] Failed to persist locationId to localStorage:', e);
    }
  }, []);

  // Available options based on user role
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  // The whole account's sites, before the SH9 site-group filter (names for records at the account's other sites)
  const [accountLocations, setAccountLocations] = useState([]);
  const [locationsLoadedForOrgId, setLocationsLoadedForOrgId] = useState(null);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reset selections when user logs out to prevent cross-user data contamination
  useEffect(() => {
    if (!currentUser) {

      setSelectedOrgIdState(null);
      setSelectedLocationIdState(null);
      setAvailableOrgs([]);
      setAvailableLocations([]);
      setAccountLocations([]);
      setLocationsLoadedForOrgId(null);
      setLoading(true);
      // Also clear localStorage
      try {
        localStorage.removeItem(STORAGE_KEY_ORG);
        localStorage.removeItem(STORAGE_KEY_LOCATION);
      } catch (e) {
        console.warn('[LocationContext] Failed to clear localStorage:', e);
      }
    }
  }, [currentUser]);

  // Sync developer's org selection - react to user.orgId changes
  // This ensures LocationContext uses the same org as AuthContext
  useEffect(() => {
    if (isDeveloper && user?.orgId && user.orgId !== selectedOrgId) {
      setSelectedOrgId(user.orgId);
      // Clear location selection AND available locations when org changes
      setSelectedLocationId(null);
      setAvailableLocations([]);
      setAccountLocations([]);
    }
  }, [isDeveloper, user?.orgId, selectedOrgId, setSelectedOrgId, setSelectedLocationId]);

  // Fetch available organizations (for developer/superuser only)
  useEffect(() => {
    const fetchOrganizations = async () => {
      // Skip if no user or if getAuthHeaders is not available
      if (!user || !getAuthHeaders || supportPicking) { // AC1: support login has not opened an account
        setLoading(false);
        return;
      }


      // Only developer and superuser can see multiple orgs
      if (!['developer', 'superuser'].includes(user.role)) {
        // Regular users only see their own org
        setAvailableOrgs([{
          id: user.orgId || 'org_001',
          name: user.orgName || 'My Organization'
        }]);
        setSelectedOrgId(user.orgId || 'org_001');
        setLoading(false);
        return;
      }

      try {
        const headers = await getAuthHeaders();
        // Check if we have valid headers before making the request
        if (!headers || !headers.Authorization) {
          throw new Error('No valid auth headers');
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/organizations`, {
          headers
        });

        if (response.status === 404) {
          // Endpoint doesn't exist yet - use default organization

          setAvailableOrgs([{
            id: 'org_001',
            name: 'Default Organization'
          }, {
            id: 'org_002',
            name: 'A1 Park & Store'
          }]);
          setSelectedOrgId('org_001');
          setLoading(false);
          return;
        }

        if (!response.ok) throw new Error('Failed to fetch organizations');

        const orgs = await response.json();
        setAvailableOrgs(orgs);

        // Auto-select first org if none selected
        if (orgs.length > 0 && !selectedOrgId) {
          setSelectedOrgId(orgs[0].id);
        }
      } catch (err) {
        console.error('Error fetching organizations:', err);
        // Fallback to default organizations on error
        setAvailableOrgs([{
          id: 'org_001',
          name: 'Default Organization'
        }, {
          id: 'org_002',
          name: 'A1 Park & Store'
        }]);
        setSelectedOrgId(user.orgId || 'org_001');
        setError(null); // Don't propagate error to prevent loading loop
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [user, supportPicking]);

  // Refetch counter to force location refresh
  const [locationRefetchCounter, setLocationRefetchCounter] = useState(0);

  // Function to refresh locations (e.g., after updating bay configuration)
  const refreshLocations = useCallback(() => {

    setLocationRefetchCounter(prev => prev + 1);
  }, []);

  // Fetch available locations based on selected org
  useEffect(() => {
    let cancelled = false;

    const fetchLocations = async () => {
      // Skip if no user or org
      if (!user || !selectedOrgId || !getAuthHeaders || supportPicking) {
        setLocationsLoadedForOrgId(null);
        return;
      }

      setLocationsLoadedForOrgId(prev => (prev === selectedOrgId ? prev : null));

      try {
        const headers = await getAuthHeaders();
        if (cancelled) return;

        // Check if we have valid headers before making the request
        if (!headers || !headers.Authorization) {
          throw new Error('No valid auth headers');
        }

        const url = new URL(`${process.env.REACT_APP_API_URL}/locations`);

        // Add org filter for developer and superuser
        if (['developer', 'superuser'].includes(user.role)) {
          url.searchParams.append('orgId', selectedOrgId);
        }

        const response = await fetch(url, {
          headers
        });
        if (cancelled) return;

        if (response.status === 404) {
          // Endpoint doesn't exist - this shouldn't happen in production
          console.error('[LocationContext] Locations endpoint not found - API may be outdated');
          setAvailableLocations([]);
          setAccountLocations([]);
          setSelectedLocationId(null);
          setLocationsLoadedForOrgId(selectedOrgId);
          setError('Locations endpoint not available');
          return;
        }

        if (!response.ok) {
          reportCompanyAccessFailure(await response.clone().json().catch(() => null));
          throw new Error('Failed to fetch locations');
        }

        const allLocations = sortByName(await response.json());
        if (cancelled) return;
        // SH9: a business chosen in the company switcher shows only the sites it runs (display filter)
        const siteGroup = selectedSiteGroup && selectedSiteGroup.orgId === selectedOrgId ? selectedSiteGroup : null;
        const locations = siteGroup ? allLocations.filter(location => siteGroup.locationIds.includes(location.id)) : allLocations;

        setAvailableLocations(locations);
        setAccountLocations(allLocations);
        setLocationsLoadedForOrgId(selectedOrgId);

        // Check if persisted selection is valid for this org
        const locationIds = locations.map(l => l.id);
        const persistedLocationValid = selectedLocationId && locationIds.includes(selectedLocationId);
        const allLocationsSelected = selectedLocationId === '';
        // Owner-share company logins always work in one shared location (SH1).
        const canUseAllLocations = ['owner', 'admin', 'developer', 'superuser'].includes(user.role) && locations.length > 1 &&
          !Array.isArray(user.shareLocations) && !siteGroup;

        // Auto-select based on user role
        if (locations.length > 0) {
          if (persistedLocationValid || (allLocationsSelected && canUseAllLocations)) {
            // Persisted location is valid, or the user explicitly chose All Locations.

          } else if (user.role === 'developer') {
            // Developer: auto-select first location so pages load data immediately

            setSelectedLocationId(locations[0].id);
          } else if (user.role === 'user') {
            // User role: select their default location, or first assigned location
            if (user.defaultLocation) {

              setSelectedLocationId(user.defaultLocation);
            } else if (user.locations?.length > 0) {

              setSelectedLocationId(user.locations[0]);
            }
          } else if (user.role === 'manager' && user.locations?.length === 1) {
            // Manager with single location: auto-select it

            setSelectedLocationId(user.locations[0]);
          } else {
            // Otherwise select first available (for non-developers)

            setSelectedLocationId(locations[0].id);
          }
        } else if (selectedLocationId) {
          // No locations available but we have a selection - clear it

          setSelectedLocationId(null);
        }
      } catch (err) {
        if (cancelled) return;

        console.error('[LocationContext] Error fetching locations:', err);
        // Don't fallback to a hardcoded location - this causes data corruption
        // Show error so user knows something is wrong
        setAvailableLocations([]);
        setAccountLocations([]);
        setSelectedLocationId(null);
        setLocationsLoadedForOrgId(selectedOrgId);
        setError('Failed to load locations. Please refresh the page.');
      }
    };

    fetchLocations();

    return () => {
      cancelled = true;
    };
  }, [user, selectedOrgId, locationRefetchCounter, selectedLocationId, getAuthHeaders, setSelectedLocationId, selectedSiteGroup, supportPicking]);

  // Check if user can access a specific location
  const canAccessLocation = useCallback((locationId) => {
    if (!user) return false;

    // Developer and superuser can access all
    if (['developer', 'superuser'].includes(user.role)) return true;

    // Owner/Admin can access all locations in their org
    if (user.role === 'owner' || user.role === 'admin') {
      const location = accountLocations.find(l => l.id === locationId);
      return location?.orgId === user.orgId;
    }

    // Manager can access assigned locations
    if (user.role === 'manager') {
      return user.locations?.includes(locationId);
    }

    // User can access assigned locations (or defaultLocation for backward compat)
    if (user.role === 'user') {
      if (user.locations?.length > 0) {
        return user.locations.includes(locationId);
      }
      return locationId === user.defaultLocation;
    }

    return false;
  }, [user, accountLocations]);

  // Check if user can switch locations
  const canSwitchLocations = useCallback(() => {
    if (!user) return false;

    // These roles can switch (owner has same access as admin)
    if (['developer', 'superuser', 'owner', 'admin'].includes(user.role)) return true;

    // Manager can switch if they have multiple locations
    if (user.role === 'manager') {
      return user.locations && user.locations.length > 1;
    }

    // User can switch if they have multiple locations assigned
    if (user.role === 'user') {
      return user.locations && user.locations.length > 1;
    }

    return false;
  }, [user]);

  // Check if user can switch organizations
  const canSwitchOrganizations = useCallback(() => {
    if (!user) return false;
    return ['developer', 'superuser'].includes(user.role);
  }, [user]);

  // Build filter object for API queries
  const getApiFilter = useCallback(() => {
    const filter = {};

    if (selectedOrgId) {
      filter.orgId = selectedOrgId;
    }

    if (selectedLocationId) {
      filter.locationId = selectedLocationId;
    }

    return filter;
  }, [selectedOrgId, selectedLocationId]);

  // Get display name for current selection
  const getLocationDisplayName = useCallback(() => {
    if (!selectedLocationId) return 'All Locations';

    const location = availableLocations.find(l => l.id === selectedLocationId);
    return location?.name || selectedLocationId;
  }, [selectedLocationId, availableLocations]);

  // SH9: ids of the sites the business picked in the company switcher runs; null when no business is picked
  const siteGroupLocationIds = useMemo(() => (
    selectedSiteGroup && selectedSiteGroup.orgId === selectedOrgId && Array.isArray(selectedSiteGroup.locationIds)
      ? selectedSiteGroup.locationIds : null
  ), [selectedSiteGroup, selectedOrgId]);

  // Name of any site in the account, including sites outside the picked business
  const getLocationName = useCallback((locationId) => {
    if (!locationId) return null;
    return accountLocations.find(l => l.id === locationId)?.name || null;
  }, [accountLocations]);

  const getOrgDisplayName = useCallback(() => {
    if (!selectedOrgId) return 'All Organizations';

    const org = availableOrgs.find(o => o.id === selectedOrgId);
    return org?.name || selectedOrgId;
  }, [selectedOrgId, availableOrgs]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // Current selections
    selectedOrgId,
    selectedLocationId,
    setSelectedOrgId,
    setSelectedLocationId,

    // Available options
    availableOrgs,
    availableLocations,
    accountLocations,
    siteGroupLocationIds,
    locationsLoadedForOrgId,

    // Permissions
    canAccessLocation,
    canSwitchLocations,
    canSwitchOrganizations,

    // Utilities
    getApiFilter,
    getLocationDisplayName,
    getLocationName,
    getOrgDisplayName,
    refreshLocations,

    // States
    loading,
    error
  }), [
    selectedOrgId, selectedLocationId, availableOrgs, availableLocations, accountLocations, siteGroupLocationIds,
    setSelectedOrgId, setSelectedLocationId,
    canAccessLocation, canSwitchLocations, canSwitchOrganizations,
    getApiFilter, getLocationDisplayName, getLocationName, getOrgDisplayName, refreshLocations,
    loading, error, locationsLoadedForOrgId
  ]);

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;
